/**
 * Height computation for the virtualized search listbox.
 *
 * Kept out of Search.jsx so the arithmetic can be unit-tested without mounting
 * react-window. The rule (issue #228): the frame shows at most 8 entities plus
 * whatever section headers are present, and shrinks to the exact content below
 * that cap. Past the cap, react-window scrolls natively (the vertical
 * scrollbar of decision 1b).
 */

/** Height in px of a section header row (Communes / EPCI) */
export const GROUP_SIZE = 48;

/** Maximum number of entity rows shown before the frame starts scrolling */
export const MAX_VISIBLE_ENTITIES = 8;

/**
 * Whether a flattened list child is a section header rather than an entity row
 * @param {Object} child - Flattened react-window item
 * @returns {boolean} True for a section header
 */
export const isGroup = (child) => Object.hasOwn(child, 'group');

/**
 * Compute the visible height of the results frame.
 * @param {Array<Object>} itemData - Flattened rows (headers and entities)
 * @param {number} itemSize - Height of an entity row (responsive: 36 or 48)
 * @returns {number} Height in px: exact content, capped at 8 entities + headers
 */
export const computeListHeight = (itemData, itemSize) => {
  const entityCount = itemData.filter((item) => !isGroup(item)).length;
  const headersHeight = itemData.filter(isGroup).length * GROUP_SIZE;

  const natural = entityCount * itemSize + headersHeight;
  const cap = MAX_VISIBLE_ENTITIES * itemSize + headersHeight;

  return Math.min(natural, cap);
};
